import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, ExternalLink } from 'lucide-react';
import { company } from '../../mock';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FindUs = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      toast({ title: 'Missing details', description: 'Email and message are required.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: 'Enquiry sent!', description: 'Thanks for reaching out. Our team will respond shortly.' });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again, or call us directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-3">Find us & get in touch</h2>
          <p className="font-body text-slate-600">Send us a message and we'll get back to you quickly.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="rounded-2xl overflow-hidden shadow-md h-72 mb-6">
              <iframe
                title="map"
                src="https://www.google.com/maps?q=8+Yorke+Street,+Dundalk,+Co.+Louth,+Ireland&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
            <a href={company.mapUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-brand-blue font-semibold hover:text-brand-orange transition-colors duration-200 mb-6">
              View on Google Map <ExternalLink className="w-4 h-4" />
            </a>
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-brand-blue-light flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-brand-blue" /></div>
                <div><div className="font-heading font-semibold text-slate-900">Address</div><div className="text-slate-600 text-sm">{company.address}</div></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-brand-blue-light flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-brand-blue" /></div>
                <div><div className="font-heading font-semibold text-slate-900">Phone</div>{company.phones.map((p) => <div key={p} className="text-slate-600 text-sm">{p}</div>)}</div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-brand-blue-light flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-brand-blue" /></div>
                <div><div className="font-heading font-semibold text-slate-900">Email</div><div className="text-slate-600 text-sm">{company.email}</div></div>
              </div>
            </div>
          </div>

          <div className="bg-brand-blue-light rounded-2xl p-8">
            <h3 className="font-heading font-bold text-2xl text-slate-900 mb-6">Service Enquiry</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input name="name" value={form.name} onChange={handleChange} placeholder="Name" className="h-12 bg-white border-slate-200" />
              <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email *" className="h-12 bg-white border-slate-200" />
              <Input name="phone" value={form.phone} onChange={handleChange} placeholder="Your Phone Number" className="h-12 bg-white border-slate-200" />
              <Textarea name="message" value={form.message} onChange={handleChange} placeholder="Comment or Message *" rows={4} className="bg-white border-slate-200" />
              <Button type="submit" disabled={submitting} className="bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold h-12 px-8 rounded-full disabled:opacity-70">{submitting ? 'Sending…' : 'Send Request'}</Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FindUs;
